import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { MailboxService } from '../services/mailbox.service';
import {
  MailboxConnection,
  CreateMailboxConnectionRequest
} from '../interfaces/mailbox-connection.interface';
import { MailboxProvider } from '../interfaces/mailbox-provider.interface';

@Component({
  selector: 'app-mailbox',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './mailbox-page.html',
  styleUrls: ['./mailbox-page.css']
})
export class MailboxPageComponent implements OnInit, OnDestroy {
  @Output() backClick = new EventEmitter<void>();

  activeTab: 'configurations' | 'connectors' = 'configurations';

  // ==========================
  // Configurations Tab
  // ==========================

  connections: MailboxConnection[] = [];
  configurationsLoading = true;
  configurationsError: string | null = null;

  // ==========================
  // Connectors Tab
  // ==========================

  providers: MailboxProvider[] = [];
  connectorsLoading = true;
  connectorsError: string | null = null;
  // ==========================
  // Connection Modal
  // ==========================

  showConnectionModal = false;
  selectedProvider: MailboxProvider | null = null;
  connectionEmail = '';
  connectionDisplayName = '';
  isConnecting = false;

  // ==========================
  // Email Viewing
  // ==========================

  selectedConnectionId: number | null = null;
  emails: any[] = [];
  emailsLoading = false;
  emailsError: string | null = null;

  // ==========================
  // Email Sync
  // ==========================

  syncingConnectionId: number | null = null;
  lastSyncConnectionId: number | null = null;
  syncStatus: string | null = null;
  syncError: string | null = null;

  // ==========================
  // Clear Mailbox
  // ==========================

  clearingMailboxId: number | null = null;
  lastClearConnectionId: number | null = null;
  clearMailboxStatus: string | null = null;
  clearMailboxError: string | null = null;

  private subscriptions = new Subscription();

  constructor(private mailboxService: MailboxService) {}

  ngOnInit(): void {
    this.loadConnections();
    this.loadProviders();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onBackClick(): void {
    this.backClick.emit();
  }

  switchTab(tab: 'configurations' | 'connectors'): void {
    this.activeTab = tab;
  }

  // =====================================================
  // CONFIGURATIONS
  // =====================================================

  loadConnections(): void {
    this.configurationsLoading = true;
    this.configurationsError = null;

    this.subscriptions.add(
      this.mailboxService.getConnections().subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.connections = response.data ?? [];
          } else {
            this.configurationsError =
              response.message || 'Failed to load connections';
          }

          this.configurationsLoading = false;
        },
        error: (err) => {
          this.configurationsError =
            'Failed to load mailbox connections';

          this.configurationsLoading = false;

          console.error('Error loading connections:', err);
        }
      })
    );
  }

  deleteConnection(id: number): void {
    if (!confirm('Are you sure you want to disconnect this mailbox?')) {
      return;
    }

    this.subscriptions.add(
      this.mailboxService.deleteConnection(id).subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.connections = this.connections.filter(
              c => c.id !== id
            );
          }
        },
        error: (err) => {
          console.error('Error deleting connection:', err);
          alert('Failed to delete connection. Please try again.');
        }
      })
    );
  }

  toggleEmails(connectionId: number): void {
    if (this.selectedConnectionId === connectionId) {
      this.closeEmails();
      return;
    }

    this.selectedConnectionId = connectionId;
    this.emails = [];
    this.emailsError = null;
    this.emailsLoading = true;

    this.subscriptions.add(
      this.mailboxService.getMessages(connectionId).subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.emails = response.data ?? [];
          } else {
            this.emailsError = response.message || 'Failed to load emails';
          }
          this.emailsLoading = false;
        },
        error: (err) => {
          this.emailsError = 'Failed to load emails. Please try again.';
          this.emailsLoading = false;
          console.error('Error loading emails:', err);
        }
      })
    );
  }

  closeEmails(): void {
    this.selectedConnectionId = null;
    this.emails = [];
    this.emailsError = null;
    this.emailsLoading = false;
  }

  syncEmails(connectionId: number): void {
    if (this.syncingConnectionId === connectionId) {
      return;
    }

    this.syncingConnectionId = connectionId;
    this.lastSyncConnectionId = connectionId;
    this.syncStatus = 'Syncing emails to blob storage...';
    this.syncError = null;

    this.subscriptions.add(
      this.mailboxService.syncMailbox(connectionId).subscribe({
        next: (response) => {
          this.syncingConnectionId = null;

          if (response.isSuccess) {
            const payload = response.data as any;
            const syncedCount = payload?.syncedMessageCount ?? payload?.SyncedMessageCount ?? 0;
            const failedCount = payload?.failedMessageCount ?? payload?.FailedMessageCount ?? 0;
            const totalCount = payload?.discoveredMessageCount ?? payload?.DiscoveredMessageCount ?? syncedCount;

            if (failedCount > 0) {
              this.syncStatus = null;
              this.syncError = `Synced ${syncedCount} of ${totalCount} email(s). ${failedCount} failed.`;
            } else {
              this.syncStatus = `Successfully synced ${syncedCount} email(s) to blob storage.`;
              this.syncError = null;
            }
          } else {
            this.syncError = response.message || 'Sync completed with no results.';
            this.syncStatus = null;
          }
        },
        error: (err) => {
          this.syncingConnectionId = null;
          this.syncStatus = null;
          this.syncError = 'Failed to sync emails. Please try again.';
          console.error('Error syncing mailbox:', err);
        }
      })
    );
  }

  clearMailbox(connectionId: number): void {
    if (!confirm('Are you sure you want to clear all emails from blob storage and the mailbox? This action cannot be undone.')) {
      return;
    }

    this.clearingMailboxId = connectionId;
    this.lastClearConnectionId = connectionId;
    this.clearMailboxStatus = 'Clearing all emails...';
    this.clearMailboxError = null;

    this.subscriptions.add(
      this.mailboxService.clearMailbox(connectionId).subscribe({
        next: (response) => {
          this.clearingMailboxId = null;

          if (response.isSuccess) {
            this.clearMailboxStatus = 'The Mailbox is cleared.';
            this.clearMailboxError = null;
          } else {
            this.clearMailboxStatus = null;
            this.clearMailboxError = response.message || 'Failed to clear emails.';
          }
        },
        error: (err) => {
          this.clearingMailboxId = null;
          this.clearMailboxStatus = null;
          this.clearMailboxError = 'Failed to clear emails. Please try again.';
          console.error('Error clearing mailbox:', err);
        }
      })
    );
  }

  testConnection(id: number): void {
    this.subscriptions.add(
      this.mailboxService.testConnection(id).subscribe({
        next: (response) => {
          if (response.isSuccess && response.data) {
            alert('Connection test successful!');
          } else {
            alert(
              'Connection test failed: ' +
              (response.message || 'Unknown error')
            );
          }
        },
        error: (err) => {
          console.error('Error testing connection:', err);
          alert('Failed to test connection. Please try again.');
        }
      })
    );
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Connected':
        return 'status-connected';

      case 'Disconnected':
        return 'status-disconnected';

      case 'Error':
        return 'status-error';

      default:
        return '';
    }
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) {
      return 'Never';
    }

    return new Date(dateStr).toLocaleString();
  }

  // =====================================================
  // CONNECTORS
  // =====================================================

  loadProviders(): void {
    this.connectorsLoading = true;
    this.connectorsError = null;

    this.subscriptions.add(
      this.mailboxService.getProviders().subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.providers = response.data ?? [];
          } else {
            this.connectorsError =
              response.message || 'Failed to load providers';
          }

          this.connectorsLoading = false;
        },
        error: (err) => {
          this.connectorsError =
            'Failed to load mailbox providers';

          this.connectorsLoading = false;

          console.error('Error loading providers:', err);
        }
      })
    );
  }

  openConnectModal(provider: MailboxProvider): void {
    this.selectedProvider = provider;
    this.connectionEmail = '';
    this.connectionDisplayName = '';
    this.showConnectionModal = true;
  }

  closeConnectModal(): void {
    this.showConnectionModal = false;
    this.selectedProvider = null;
    this.connectionEmail = '';
    this.connectionDisplayName = '';
    this.isConnecting = false;
  }

  connectMailbox(): void {
    if (!this.selectedProvider || !this.connectionEmail.trim()) {
      return;
    }

    this.isConnecting = true;

    const request: CreateMailboxConnectionRequest = {
      providerId: this.selectedProvider.id,
      emailAddress: this.connectionEmail.trim(),
      displayName:
        this.connectionDisplayName.trim() || undefined,
      configurationJson: '{}'
    };

    this.subscriptions.add(
      this.mailboxService.createConnection(request).subscribe({
        next: (response) => {
          this.isConnecting = false;

          if (response.isSuccess) {
            alert('Mailbox connected successfully!');

            this.closeConnectModal();

            // Refresh configuration list
            this.loadConnections();
          } else {
            alert(
              'Failed to connect: ' +
              (response.message || 'Unknown error')
            );
          }
        },
        error: (err) => {
          this.isConnecting = false;

          console.error('Error connecting mailbox:', err);

          alert(
            'Failed to connect mailbox. Please try again.'
          );
        }
      })
    );
  }

  getProviderIcon(provider: MailboxProvider): string {
    switch (provider.providerType) {
      case 'Exchange':
        return '&#128231;';

      case 'Gmail':
        return '&#128231;';

      case 'Zoho':
        return '&#128231;';

      case 'Outlook':
        return '&#128187;';

      case 'ImapSmtp':
        return '&#128229;';

      default:
        return '&#9993;';
    }
  }

  isOAuthProvider(provider: MailboxProvider): boolean {
    return (
      provider.providerType === 'Zoho' ||
      provider.providerType === 'Outlook' ||
      provider.providerType === 'Gmail'
    );
  }

  connectWithOAuth(provider: MailboxProvider): void {
    this.selectedProvider = provider;
    this.isConnecting = true;

    const authUrl$ = provider.providerType === 'Outlook'
      ? this.mailboxService.getOutlookAuthorizeUrl()
      : this.mailboxService.getZohoAuthorizeUrl();

    this.subscriptions.add(
      authUrl$.subscribe({
        next: (response) => {
          this.isConnecting = false;

          if (response.authorizationUrl) {
            window.location.href =
              response.authorizationUrl;
          } else {
            alert('Failed to get authorization URL');
          }
        },
        error: (err) => {
          this.isConnecting = false;

          console.error(
            'Error getting authorize URL:',
            err
          );

          alert(
            'Failed to initiate OAuth flow. Please try again.'
          );
        }
      })
    );
  }
}